import {
  useState,
  useEffect,
  useContext,
  createContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { catalogConfigs as configs } from "configs";

const CatalogContext = createContext();

export const CatalogProvider = ({ children }) => {
  const navigate = useNavigate();
  const { lang: langParam, pageName, nestedPageName } = useParams();
  const { langSet, pageMap, breakpoint } = configs;
  const lang = langSet.includes(langParam) ? langParam : langSet[0];

  const pageFlatMap = useMemo(() => {
    return pageMap[lang]?.reduce((acc, page) => {
      if (page.outlets) {
        page.outlets.forEach((outlet) => acc.push(`${page.name}/${outlet}`));
      } else {
        acc.push(page.name);
      }
      return acc;
    }, []);
  }, [pageMap, lang]);

  const defaultPath = `/${lang}/${pageFlatMap[0]}`;
  const curPagePath = nestedPageName ? `${pageName}/${nestedPageName}` : pageName;
  const pageIndex = pageFlatMap.indexOf(curPagePath) || 0;
  const isFirstPage = pageIndex === 0;
  const isLastPage = pageIndex === pageFlatMap.length - 1;

  const [autoplayState, setAutoplayState] = useState(false);
  const autoplayTimer = useRef(null);

  const [pagingPending, setPagingPending] = useState(false);
  const pagingDebounce = useRef(null);

  const handleAutoplay = (action) => {
    if (action === "toggle") {
      setAutoplayState((prev) => !prev);
    } else {
      setAutoplayState(action);
    }
  };

  const paging = useCallback(
    (action, path, isAutoPaging) => {
      if (!isAutoPaging) handleAutoplay(false);

      let targetPage;

      switch (action) {
        case "prev":
          targetPage = pageFlatMap[pageIndex - 1];
          break;
        case "next":
          targetPage = pageFlatMap[pageIndex + 1];
          break;
        default:
          targetPage = path;
      }

      if (targetPage) navigate(`/${lang}/${targetPage}`);
    },
    [lang, pageIndex, pageFlatMap],
  );

  const [isResponsive, setIsResponsive] = useState(false);
  const [activePageInfo, setActivePageInfo] = useState(null);

  useEffect(() => {
    const resizeHandler = () => {
      setIsResponsive(breakpoint >= window.innerWidth);
    };

    resizeHandler();

    window.addEventListener("resize", resizeHandler);

    return () => {
      window.removeEventListener("resize", resizeHandler);
    };
  }, [breakpoint]);

  useEffect(() => {
    if (!lang || !pageName || !langSet.includes(lang) || !pageFlatMap.includes(curPagePath)) {
      navigate(defaultPath);
    }
  }, [lang, curPagePath, defaultPath, langSet, pageFlatMap, pageName]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === " ") {
        handleAutoplay("toggle");
      } else {
        handleAutoplay(false);
      }

      if (["ArrowLeft", "ArrowUp"].includes(e.key)) {
        paging("prev");
      } else if (["ArrowRight", "ArrowDown"].includes(e.key)) {
        paging("next");
      }
    };

    const handleWheel = (e) => {
      if (isResponsive) return;

      if (pagingPending) return;

      setPagingPending(true);

      handleAutoplay(false);

      paging(e.deltaY > 0 ? "next" : "prev");

      clearTimeout(pagingDebounce.current);

      pagingDebounce.current = setTimeout(() => {
        setPagingPending(false);
      }, 800);
    };

    const handleClick = (e) => {
      if (!e.target.closest('[data-role="autoplayButton"]')) {
        handleAutoplay(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("wheel", handleWheel);
    window.addEventListener("click", handleClick);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("click", handleClick);
    };
  }, [paging, isResponsive, pagingDebounce, pagingPending]);

  useEffect(() => {
    if (!configs.autoplay) return;

    if (autoplayState) {
      autoplayTimer.current = setInterval(() => {
        if (configs.autoplay.loop && isLastPage) {
          paging("default", pageFlatMap[0], true);
        } else if (isLastPage) {
          clearInterval(autoplayTimer.current);
          setAutoplayState(false);
        } else {
          paging("next", null, true);
        }
      }, configs.autoplay.delay);
    } else {
      clearInterval(autoplayTimer.current);
    }

    return () => clearInterval(autoplayTimer.current);
  }, [autoplayState, configs, pageIndex, isLastPage, paging]);

  return (
    <CatalogContext.Provider
      value={{
        configs,
        lang,
        pageName,
        autoplayState,
        handleAutoplay,
        paging,
        isFirstPage,
        isLastPage,
        isResponsive,
        activePageInfo,
        setActivePageInfo,
      }}
    >
      {children}
    </CatalogContext.Provider>
  );
};

export const useCatalog = () => {
  return useContext(CatalogContext);
};
